/* v1.10.53 — Robot control buttons (Start / Go Home / Reset / Check Battery).
 * Self-contained: manages its own UI state + proxies every action through the
 * Desktop backend (which forwards to the main app with the per-session JWT).
 * Used on the Robot page (RobotSummary). robotId = robot's main-app Mongo _id. */
import React, { useState, useRef, useCallback } from 'react';
import { robotCommand, robotBatteryRefresh, robotBatteryLiveReport, robotBatteryStore, robotBatteryHistory } from '../services/api';

const EVENT_LINE = {
  START_CLEANING: '🟢 Drive Forward',
  GO_HOME: '🏠 Going Home',
  RESET: '🔄 Reset sent — wait 30 sec to reactivate',
};
const nowStr = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const withTimeout = (p, ms) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error('ack-timeout')), ms))]);

const btn = { padding: '6px 10px', borderRadius: 6, border: 'none', fontSize: 13, fontWeight: 600 };
const sty = {
  start: { background: '#a29bfe', color: '#fff' },
  home: { background: '#81ecec', color: '#00695c' },
  reset: { background: '#ffeaa7', color: '#d68910' },
  batt: { background: '#dfe6e9', color: '#2d3436' },
};

export default function RobotControls({ robotId, projectId, robotNumber }) {
  const [st, setStRaw] = useState({});
  const timer = useRef(null);
  const set = useCallback((patch) => setStRaw((prev) => ({ ...prev, ...patch })), []);
  const startCountdown = useCallback(() => {
    clearInterval(timer.current);
    setStRaw((prev) => ({ ...prev, locked: true, countdown: 30 }));
    timer.current = setInterval(() => {
      setStRaw((prev) => {
        const c = (prev.countdown || 0) - 1;
        if (c <= 0) { clearInterval(timer.current); return { ...prev, locked: false, countdown: 0 }; }
        return { ...prev, countdown: c };
      });
    }, 1000);
  }, []);
  const doCommand = useCallback(async (event) => {
    if (!robotId) { set({ panelOpen: true, failed: true, line: 'Missing robot id — reload' }); return; }
    set({ sending: event, panelOpen: true, failed: false, line: null });
    try {
      const res = await withTimeout(robotCommand(robotId, projectId, event), 3000);
      if (res && res.ok) { set({ sending: null, lastEvent: event, failed: false, line: `${EVENT_LINE[event]} · ${nowStr()}` }); startCountdown(); }
      else set({ sending: null, failed: true, line: 'Failed — Try Again' });
    } catch (e) { set({ sending: null, failed: true, line: 'Failed — Try Again' }); }
  }, [robotId, projectId, set, startCountdown]);
  const doBattery = useCallback(async () => {
    if (!robotId) { set({ panelOpen: true, batt: { error: true, message: 'Missing robot id' } }); return; }
    set({ sending: 'BATTERY', panelOpen: true, failed: false, batt: { loading: true } });
    try {
      const ref = await robotBatteryRefresh(robotId, projectId);
      if (!ref || !ref.ok) { set({ sending: null, batt: { offline: !!(ref && ref.offline), error: !(ref && ref.offline), message: (ref && ref.message) || 'could not start reading' } }); return; }
      startCountdown();
      let reading = null;
      for (let i = 0; i < 12 && !reading; i++) { await sleep(2500); try { const lr = await robotBatteryLiveReport(projectId, ref.processId); if (lr && lr.ready && lr.reading) reading = lr.reading; } catch (e) {} }
      if (reading) {
        const ts = new Date().toISOString();
        try { await robotBatteryStore({ robotId, projectId, robotNumber, voltage: reading.voltage, soc: reading.soc, charging_current_a: reading.charging_current_a, ts }); } catch (e) {}
        let history = []; try { const h = await robotBatteryHistory(robotId, 4, projectId, robotNumber); history = (h && h.readings) || []; } catch (e) {}
        set({ sending: null, batt: { loading: false, reading, history } });
      } else set({ sending: null, batt: { loading: false, empty: true } });
    } catch (e) { set({ sending: null, batt: { loading: false, error: true } }); }
  }, [robotId, projectId, robotNumber, set, startCountdown]);

  const busy = st.locked || !!st.sending;
  const mk = (key, label, emoji, s, onClick) => {
    const sending = st.sending === key; const acked = st.locked && st.lastEvent === key;
    return (
      <button key={key} disabled={busy} onClick={onClick} title={label}
        style={{ ...btn, ...s, opacity: busy ? 0.45 : 1, cursor: busy ? 'default' : 'pointer' }}>
        {sending ? '📤' : acked ? `✓ ${st.countdown}s` : `${emoji} ${label}`}
      </button>
    );
  };
  const b = st.batt;
  return (
    <div style={{ minWidth: 280 }}>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {mk('START_CLEANING', 'Start', '▶', sty.start, () => doCommand('START_CLEANING'))}
        {mk('GO_HOME', 'Home', '🏠', sty.home, () => doCommand('GO_HOME'))}
        {mk('RESET', 'Reset', '⟲', sty.reset, () => doCommand('RESET'))}
        {mk('BATTERY', 'Battery', '🔋', sty.batt, () => doBattery())}
      </div>
      {st.panelOpen && (
        <div style={{ marginTop: 8, padding: '8px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, textAlign: 'left' }}>
          {st.line && <div style={{ fontSize: 13, fontWeight: 600, color: st.failed ? '#c0392b' : '#1e293b', marginBottom: b ? 8 : 0 }}>{st.line}</div>}
          {b && (
            <div style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Battery — Robot {robotNumber}</div>
              {b.loading && <div>Now &nbsp;…&nbsp; refreshing…</div>}
              {b.offline && <div style={{ color: '#c0392b' }}>Now &nbsp;—&nbsp; robot offline, can't read battery</div>}
              {b.empty && <div style={{ color: '#b7791f' }}>Now &nbsp;—&nbsp; no reading yet, try again</div>}
              {b.error && <div style={{ color: '#c0392b' }}>Now &nbsp;—&nbsp; {b.message || 'could not read battery'}</div>}
              {b.reading && <div style={{ fontWeight: 600 }}>Now &nbsp; {nowStr()} &nbsp; Voltage {b.reading.voltage != null ? b.reading.voltage : '—'} V · SOC {b.reading.soc != null ? b.reading.soc : '—'} % · Charging {b.reading.charging_current_a != null ? b.reading.charging_current_a : '—'} A</div>}
              {b.history && b.history.map((h, hi) => (
                <div key={hi} style={{ color: '#64748b' }}>{h.ts ? new Date(h.ts).toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'} &nbsp; Voltage {h.voltage != null ? h.voltage : '—'} V · SOC {h.soc != null ? h.soc : '—'} % · Charging {h.charging_current_a != null ? h.charging_current_a : '—'} A</div>
              ))}
            </div>
          )}
          <button onClick={() => set({ panelOpen: false })} style={{ marginTop: 8, fontSize: 11, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>close</button>
        </div>
      )}
    </div>
  );
}
